import { InputBase } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

import SquareChevronDownIcon from '../../../assets/icons/SquareChevronDown';
import colors from '../../../constants/colors';

type Option = {
  value: string;
  display: string;
};

export interface SelectProps {
  value?: string;
  setValue?: (val?: string) => void;
  label: string;
  dataTestId: string;
  options: Option[];
  onChange?: (event: SelectChangeEvent) => void;
  hideLabel?: boolean;
  ariaControlsValue: string;
  required?: boolean;
}

function Select({
  value,
  setValue,
  label,
  dataTestId,
  options,
  onChange,
  hideLabel = false,
  ariaControlsValue,
  required = false,
}: SelectProps) {
  const [ariaControls, setAriaControls] = useState<string | undefined>(
    undefined
  );
  const handleChange = (event: SelectChangeEvent) => {
    const val = event.target.value as string;
    const found =
      options.find((option) => option.value === val)?.value || value;
    setValue && setValue(found);
  };

  return (
    <FormControl
      sx={{
        '& .MuiInputBase-root.Mui-focused': {
          borderColor: colors.csaaWhite,
          outline: `2px solid ${colors.csaaBlack}`,
        },
        '&:hover': {
          outline: `1px solid ${colors.csaaMid}`,
          borderRadius: 1,
        },
      }}
      fullWidth
    >
      {label && (
        <InputLabel
          id={`styledSelectLabel-${dataTestId}`}
          htmlFor={`styledSelectLabel-${dataTestId}`}
          sx={{
            visibility: hideLabel ? 'hidden' : 'visible',
            '&.MuiInputLabel-shrink': { top: 16, fontWeight: 700 },
            fontFamily: 'Cabin',
            '&.Mui-focused': {
              color: colors.csaaBlack,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        id={`styledSelectLabel-${dataTestId}`}
        labelId={`styledSelectLabel-${dataTestId}`}
        variant="outlined"
        data-testid={`styledSelect-${dataTestId}`}
        value={value}
        onOpen={() => setAriaControls(ariaControlsValue)}
        onClose={() => setAriaControls(undefined)}
        SelectDisplayProps={{
          role: 'combobox',
          'aria-controls': ariaControls,
        }}
        label={label}
        onChange={onChange || handleChange}
        input={<SelectField />}
        IconComponent={SquareChevronDownIcon}
        required={required}
        sx={{
          '& .MuiSelect-select': { py: 1, px: 2 },
          fontSize: { mobile: 16, tablet: 18 },
          ...(!hideLabel && { '& .MuiInputBase-input': { mt: 1 } }),
        }}
        MenuProps={{
          MenuListProps: {
            id: ariaControlsValue,
          },
        }}
      >
        {options.map((option, idx) => (
          <MenuItem key={option.value + idx} value={option.value}>
            {option.display}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
}

export default Select;

const SelectField = styled(InputBase)(({ theme }) => ({
  '&.MuiInputBase-root': {
    '& .MuiInputBase-input': {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    border: `1px solid ${colors.csaaNeutralBorder}`,
    borderRadius: 4,
    paddingRight: theme.spacing(2),
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Cabin',
    fontKerning: 'normal',
    fontWeight: 400,
    height: '1.4375em',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    color: colors.csaaMid,
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:hover': {
      backgroundColor: 'transparent',
      '&.MuiFilledInput-root:before': {
        borderBottom: 'none',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      boxShadow: `inset 0px 0px 0px 2px ${colors.csaaBlack}`,
      borderColor: 'transparent',
      '&:after': {
        borderBottom: 'none',
      },
      '&.Mui-error': {
        boxShadow: `inset 0px 0px 0px 2px ${theme.palette.error.main}`,
      },
    },
    '&:after': {
      borderBottom: 'none',
    },
    '&:before': {
      borderBottom: 'none',
    },
    '&.Mui-error': {
      border: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInputBase-input': {
      paddingBottom: theme.spacing(0.5),
    },
    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    'input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
  '& .MuiInputLabel-root ': {
    '&.MuiFormLabel-filled': {
      fontWeight: 700,
    },
    '&.Mui-focused': {
      fontWeight: 700,
    },
  },
}));
